import React, { useState } from 'react';
import {
  Activity,
  RefreshCw,
  AlertTriangle,
  Database,
  FileText,
  HardDrive,
  Layers,
  Clock,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useIngestDashboard, type IngestError } from '../hooks/useIngestDashboard';

const ALLOWED_EMAILS = ['nitintangellamudi@gmail.com'];

interface IngestDashboardPageProps {
  userEmail: string | null;
}

export const IngestDashboardPage: React.FC<IngestDashboardPageProps> = ({ userEmail }) => {
  if (!userEmail || !ALLOWED_EMAILS.includes(userEmail)) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <ShieldAlert className="w-16 h-16 text-red-400 mb-4" />
        <h1 className="text-2xl font-bold text-black mb-2">Not Authorized</h1>
        <p className="text-gray-600">This page is restricted to authorized users.</p>
      </div>
    );
  }

  return <DashboardContent />;
};

function DashboardContent() {
  const { data, loading, error, refresh, autoRefresh, setAutoRefresh } = useIngestDashboard();
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refresh();
    setLastRefreshed(new Date());
    setRefreshing(false);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-32">
        <RefreshCw className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (error === 'Forbidden') {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <ShieldAlert className="w-16 h-16 text-red-400 mb-4" />
        <h1 className="text-2xl font-bold text-black mb-2">Forbidden</h1>
        <p className="text-gray-600">Your account does not have access to this dashboard.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <AlertTriangle className="w-16 h-16 text-yellow-500 mb-4" />
        <h1 className="text-2xl font-bold text-black mb-2">Error</h1>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-r from-green-500/20 to-blue-500/20 border border-green-300/50">
            <Activity className="w-8 h-8 text-green-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-black">Ingest Dashboard</h1>
            <p className="text-sm text-gray-500">
              Last refreshed: {lastRefreshed.toLocaleTimeString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            Auto-refresh (15s)
          </label>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 hover:border-green-400 hover:text-green-700 transition-all shadow-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {data && (
        <div className="space-y-6">
          {/* Job Queue */}
          <StatCard
            title="Job Queue"
            icon={<Layers className="w-5 h-5 text-blue-600" />}
            counts={data.jobs}
            colorMap={JOB_COLORS}
          />

          {/* Filing Status — two cards side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <StatCard
              title="Filings — Text Status"
              icon={<FileText className="w-5 h-5 text-green-600" />}
              counts={data.filings.text_status}
              colorMap={FILING_TEXT_COLORS}
            />
            <StatCard
              title="Filings — PDF Status"
              icon={<HardDrive className="w-5 h-5 text-purple-600" />}
              counts={data.filings.pdf_status}
              colorMap={FILING_PDF_COLORS}
            />
          </div>

          {/* Docket + Backfill — two cards side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <StatCard
              title="Docket PDF Ingest"
              icon={<Database className="w-5 h-5 text-orange-600" />}
              counts={data.dockets.pdf_ingest_status}
              colorMap={DOCKET_COLORS}
            />
            <StatCard
              title="Backfill Seed"
              icon={<Clock className="w-5 h-5 text-teal-600" />}
              counts={data.backfill_seed}
              colorMap={BACKFILL_COLORS}
            />
          </div>

          {/* Recent Errors */}
          <ErrorsTable errors={data.recent_errors} />
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Stat Card                                                         */
/* ------------------------------------------------------------------ */

interface StatCardProps {
  title: string;
  icon: React.ReactNode;
  counts: Record<string, number>;
  colorMap: Record<string, string>;
}

function StatCard({ title, icon, counts, colorMap }: StatCardProps) {
  const total = Object.values(counts).reduce((s, n) => s + n, 0);
  const entries = Object.entries(counts).sort(([, a], [, b]) => b - a);

  return (
    <div className="bg-white border-2 border-gray-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-lg font-bold text-black">{title}</h3>
        </div>
        <span className="text-sm font-semibold text-gray-500">Total: {total.toLocaleString()}</span>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-gray-400 italic">No data</p>
      ) : (
        <div className="space-y-2">
          {entries.map(([key, value]) => {
            const pct = total > 0 ? (value / total) * 100 : 0;
            const color = colorMap[key] || 'bg-gray-300';
            return (
              <div key={key}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium text-gray-700">{key}</span>
                  <span className="font-semibold text-gray-900">
                    {value.toLocaleString()}
                    <span className="text-gray-400 ml-1 text-xs">({pct.toFixed(1)}%)</span>
                  </span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${color} transition-all duration-500`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Errors Table                                                      */
/* ------------------------------------------------------------------ */

function ErrorsTable({ errors }: { errors: IngestError[] }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="bg-white border-2 border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <h3 className="text-lg font-bold text-black">Recent Errors</h3>
          <span className="text-sm text-gray-500">({errors.length})</span>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="overflow-x-auto">
          {errors.length === 0 ? (
            <p className="px-5 pb-5 text-sm text-gray-400 italic">No recent errors</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-t border-gray-100 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <th className="px-5 py-3">Pipeline</th>
                  <th className="px-5 py-3">Docket</th>
                  <th className="px-5 py-3">Error</th>
                  <th className="px-5 py-3 whitespace-nowrap">Failed At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {errors.map((err, i) => (
                  <tr key={err.id ?? i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">
                        {err.pipeline}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">
                      {err.docket_number || '—'}
                    </td>
                    <td className="px-5 py-3 text-gray-700 max-w-md truncate" title={err.error_message}>
                      {err.error_message}
                    </td>
                    <td className="px-5 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(err.failed_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Color maps for progress bars                                      */
/* ------------------------------------------------------------------ */

const JOB_COLORS: Record<string, string> = {
  pending: 'bg-yellow-400',
  running: 'bg-blue-500',
  completed: 'bg-green-500',
  failed: 'bg-red-500',
  permanently_failed: 'bg-red-700',
};

const FILING_TEXT_COLORS: Record<string, string> = {
  pending: 'bg-yellow-400',
  extracting: 'bg-blue-400',
  extracted: 'bg-green-500',
  failed: 'bg-red-500',
  permanently_failed: 'bg-red-700',
  not_available: 'bg-gray-400',
};

const FILING_PDF_COLORS: Record<string, string> = {
  not_requested: 'bg-gray-300',
  queued: 'bg-yellow-400',
  uploading: 'bg-blue-400',
  uploaded: 'bg-green-500',
  failed: 'bg-red-500',
  permanently_failed: 'bg-red-700',
};

const DOCKET_COLORS: Record<string, string> = {
  not_requested: 'bg-gray-300',
  queued: 'bg-yellow-400',
  in_progress: 'bg-blue-500',
  complete: 'bg-green-500',
  failed: 'bg-red-500',
};

const BACKFILL_COLORS: Record<string, string> = {
  pending: 'bg-yellow-400',
  in_progress: 'bg-blue-500',
  complete: 'bg-green-500',
  failed: 'bg-red-500',
};
